import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Hide, Text } from '@atlaskit/primitives/compiled';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { css } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { spotlight, editorBpqaSpotlight } from '../../messages.ts';
import { useTour } from '../../hooks/useNthUserOrientationStore.ts';
import { useSpotlightAnalytics } from '../../hooks/useSpotlightAnalytics.ts';

// Override the headline size to match the intended Spotlight sizing.
// The editor's h5 CSS selector has higher specificity than Spotlight's default
const headlineOverride = css({
	font: token('font.heading.xsmall'),
});

interface EditorBpqaSpotlightProps {
	children: ReactNode;
}
const STEP_ID = 'nth-user-orientation-editor-bpqa-spotlight';
export const EditorBpqaSpotlight: React.FC<EditorBpqaSpotlightProps> = ({ children }) => {
	const { reset, back, currentStep, progress, hasInteracted } = useTour(STEP_ID);
	const { formatMessage } = useIntl();
	const isVisible = currentStep === STEP_ID;
	const { PopoverContent } = useChoreographerAssets();

	const { handleNext, handleBack, handleDismiss } = useSpotlightAnalytics({
		spotlightId: STEP_ID,
		isVisible,
		onNext: reset,
		onBack: back,
		onDismiss: reset,
	});

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<Hide below="md">
				<PopoverContent
					shouldDismissOnClickOutside={false}
					back={handleBack}
					next={handleNext}
					dismiss={handleDismiss}
					placement="top-end"
					offset={[0, 32]}
					isVisible={isVisible}
					messageId={STEP_ID}
					messageType={hasInteracted ? 'transactional' : 'engagement'}
					strategy="absolute"
				>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId={STEP_ID}>
								<SpotlightHeader>
									<SpotlightHeadline>
										{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
										<span css={headlineOverride}>
											{formatMessage(editorBpqaSpotlight.headline)}
										</span>
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>
									<Text>{formatMessage(editorBpqaSpotlight.body)}</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightStepCount>
										{formatMessage(spotlight.progress, progress)}
									</SpotlightStepCount>
									<SpotlightActions>
										<SpotlightSecondaryAction>
											{formatMessage(spotlight.back)}
										</SpotlightSecondaryAction>
										<SpotlightPrimaryAction>{formatMessage(spotlight.done)}</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						return fg('platform-dst-motion-uplift') ? (
							spotlightCard
						) : (
							<FadeIn entranceDirection="bottom">
								{(props) => <div {...props}>{spotlightCard}</div>}
							</FadeIn>
						);
					})()}
				</PopoverContent>
			</Hide>
		</PopoverProvider>
	);
};
